import { FunctionComponent, useCallback, useContext, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./CoownerAssemblyItem.module.css";
import { ScreenWidth } from "src/stores/UIStore";
import { Tiles } from "src/components";
import { useMutation } from "@apollo/client";
import { ASSEMBLY_AUDIT_EVENT } from "src/api/assembly";
import { getTime, getDateWithLongMonth } from "src/utils/Date";
import { UPSERT_ASSEMBLY_USER_AUDITS } from "src/api/assembly";
import {
    UpsertAssemblyUserAudits,
    UpsertAssemblyUserAuditsVariables
} from "src/api/generated/UpsertAssemblyUserAudits";
import AssemblyDocuments from "./document/AssemblyDocuments";
import AssemblyDeadlines from "./deadlines/AssembliesDeadlines";
import AssemblyAgendaItems from "./agenda/AssembliesAgendaItems";
import { cond_assemblyuseraudits_insert_input } from "src/api/generated/globalTypes";
import DownloadCloud from "src/assets/images/common/DownloadCloud.png";
import { v4 as uuidv4 } from "uuid";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";

type CoownerAssemblyItemProps = {
    realestateid: string;
};

const CoownerAssemblyItemBase: FunctionComponent<CoownerAssemblyItemProps> = (props) => {
    const { t } = useTranslation();
    const { uiStore, assemblyStore, agendaitemStore } = useContext(RootStoreContext);
    const [hasCoownerViewedAssembly, setHasCoownerViewedAssembly] = useState<boolean>(false);
    const { realestateid } = props;

    const [upsertAssemblyUserAuditMutation] = useMutation<UpsertAssemblyUserAudits, UpsertAssemblyUserAuditsVariables>(
        UPSERT_ASSEMBLY_USER_AUDITS
    );

    const onDownloadAgendaItemDocuments = useCallback(async () => {
        agendaitemStore.downloadAgendaItemFiles();
    }, [agendaitemStore]);

    const renderTooltip = useMemo(
        () => (
            <UI.Tooltip id="downloadDocumentTooltip">
                {t("screens.realestate.assembly.download_file_tootlip")}
            </UI.Tooltip>
        ),
        [t]
    );

    const DownloadAgendItemDocument = useCallback(() => {
        return (
            <div
                className={`${styles.SidebarIconTitleContainer} ${styles.TooltipContainer} d-flex align-items-center ${
                    uiStore.isMobile ? styles.Mobile : ""
                }`}
            >
                {agendaitemStore.isDownloadAgendaItemFiles ? (
                    <UI.RotatingSpinner noLogo={true} size={25} />
                ) : (
                    <UI.OverlayTrigger key={"downloadDocumentTooltip2"} placement="right" overlay={renderTooltip}>
                        <UI.BootstrapButton variant="link" style={{ paddingRight: 0, borderRightWidth: 0 }}>
                            <img
                                src={DownloadCloud}
                                style={{ width: 30, alignSelf: "center" }}
                                onClick={onDownloadAgendaItemDocuments}
                                alt=""
                            />
                        </UI.BootstrapButton>
                    </UI.OverlayTrigger>
                )}
            </div>
        );
    }, [agendaitemStore.isDownloadAgendaItemFiles, onDownloadAgendaItemDocuments, renderTooltip, uiStore.isMobile]);

    if (!hasCoownerViewedAssembly && assemblyStore.currentAssembly !== undefined) {
        const upsertAssemblyUserAuditObject: cond_assemblyuseraudits_insert_input[] = [
            {
                assemblyid: assemblyStore.currentAssembly?.id,
                event: ASSEMBLY_AUDIT_EVENT.VIEWED,
                timestamp: new Date(Date.now())
            }
        ];

        upsertAssemblyUserAuditMutation({
            variables: {
                objects: upsertAssemblyUserAuditObject
            }
        });

        setHasCoownerViewedAssembly(true);
    }

    const InfoCardAssemblyData = () => {
        if (assemblyStore.currentAssembly !== undefined && assemblyStore.currentAssembly.date) {
            const dateFromDB = assemblyStore.currentAssembly.date;
            const date = getDateWithLongMonth(dateFromDB);
            const time = getTime(dateFromDB);

            const dateWithTime = `${date} - ${time}`;

            return (
                <UI.Row className={styles.InfoCardValues}>
                    <div>{dateWithTime}</div>
                </UI.Row>
            );
        }

        return <UI.Row className={styles.InfoCardValues}></UI.Row>;
    };

    const InfoCardAssemblyPlace = () => {
        if (
            assemblyStore.currentAssembly !== undefined &&
            assemblyStore.currentAssembly.street &&
            assemblyStore.currentAssembly.housenumber
        ) {
            const assembly = assemblyStore.currentAssembly;
            const formatAddress = `${assembly.street} ${assembly.housenumber}, ${assembly.zip} ${assembly.city}`;

            return (
                <UI.Row className={styles.InfoCardValues}>
                    <div className={styles.CityLocationValue}>{assembly.locationdetail}</div>
                    <div className={styles.CityLocationValue}>{formatAddress}</div>
                </UI.Row>
            );
        }

        return <UI.Row className={styles.InfoCardValues}></UI.Row>;
    };

    const AssemblyInfoCard = () => {
        if (assemblyStore.currentAssembly !== undefined) {
            return (
                <UI.Card className={styles.InfoCard} title={t("screens.realestate.assembly.info")}>
                    <div className={styles.InfoCardDetails}>{t("screens.realestate.assembly.date")}</div>
                    <InfoCardAssemblyData />
                    <div className={styles.InfoCardDetails}>{t("screens.realestate.assembly.city")}</div>
                    <InfoCardAssemblyPlace />
                </UI.Card>
            );
        } else {
            return (
                <UI.Card className={styles.InfoCard} title={t("screens.realestate.assembly.info")}>
                    <div>{t("screens.realestate.assembly.no_assambly")}</div>
                </UI.Card>
            );
        }
    };

    const AssemblyTitle = () => {
        if (assemblyStore.currentAssembly !== undefined) {
            const title = assemblyStore.currentAssembly.title;
            const date = assemblyStore.currentAssembly.date;

            return <UI.Title>{`${title} am ${getDateWithLongMonth(date)}`}</UI.Title>;
        } else {
            return <UI.Title>{t("screens.realestate.assemblies.title")}</UI.Title>;
        }
    };

    const AssemblyDocumentsCard = () => {
        if (assemblyStore.currentAssembly !== undefined) {
            const assemblyId = assemblyStore.currentAssembly.id;

            return (
                <AssemblyDocuments
                    assemblyid={assemblyId ?? ""}
                    assemblyTitle={assemblyStore.currentAssembly.title}
                    assemblyDate={assemblyStore.currentAssembly.date}
                />
            );
        } else {
            return <></>;
        }
    };

    const AssemblyAgendaCard = () => {
        if (assemblyStore.currentAssembly !== undefined) {
            const assemblyId = assemblyStore.currentAssembly.id;

            return (
                <UI.Card
                    className={styles.AgendaCard}
                    title={t("screens.realestate.assembly.agenda").toString()}
                    icon={
                        assemblyStore.currentAssembly.state !== ASSEMBLY_STATE.DATE_PUBLISHED ? (
                            <DownloadAgendItemDocument />
                        ) : undefined
                    }
                >
                    {assemblyStore.currentAssembly.state !== ASSEMBLY_STATE.DATE_PUBLISHED ? (
                        <AssemblyAgendaItems assemblyid={assemblyId ?? ""} key={uuidv4()} />
                    ) : (
                        <div className={styles.AgendaItemsPreparing}>
                            {t("screens.realestate.assembly.agenda_preparing")}
                        </div>
                    )}
                </UI.Card>
            );
        } else {
            return <></>;
        }
    };

    const AssemblyDeadlinesCard = () => {
        if (assemblyStore.currentAssembly !== undefined) {
            const assemblyId = assemblyStore.currentAssembly.id;

            return <AssemblyDeadlines assemblyid={assemblyId ?? ""} />;
        } else {
            return <></>;
        }
    };

    return (
        <UI.Container className="pt-4">
            <AssemblyTitle />
            <UI.Row>
                {window.innerWidth >= ScreenWidth.XL ? (
                    <>
                        <UI.Col xl={3}>
                            <AssemblyInfoCard />
                            <AssemblyDocumentsCard />
                        </UI.Col>
                        <UI.Col xl={6}>
                            <AssemblyAgendaCard />
                        </UI.Col>
                    </>
                ) : window.innerWidth >= ScreenWidth.LG ? (
                    <>
                        <UI.Col lg={4}>
                            <AssemblyInfoCard />
                        </UI.Col>
                        <UI.Col lg={4}>
                            <AssemblyDeadlinesCard />
                        </UI.Col>
                        <UI.Col lg={4}>
                            <AssemblyDocumentsCard />
                        </UI.Col>

                        <UI.Col xl={6}>
                            <AssemblyAgendaCard />
                        </UI.Col>
                    </>
                ) : (
                    <>
                        <UI.Col lg={4}>
                            <AssemblyInfoCard />
                        </UI.Col>
                        <UI.Col lg={4}>
                            <AssemblyDocumentsCard />
                        </UI.Col>
                        <UI.Col lg={4}>
                            <AssemblyAgendaCard />
                        </UI.Col>
                    </>
                )}

                {window.innerWidth >= ScreenWidth.XL ? (
                    <UI.Col xl={3}>
                        <AssemblyDeadlinesCard />
                        <Tiles.ContactTile realestateid={realestateid} />
                    </UI.Col>
                ) : (
                    <>
                        {window.innerWidth >= ScreenWidth.LG ? undefined : (
                            <UI.Col lg={4}>
                                <AssemblyDeadlinesCard />
                            </UI.Col>
                        )}
                        <UI.Col lg={4}>
                            <Tiles.ContactTile realestateid={realestateid} />
                        </UI.Col>
                    </>
                )}
            </UI.Row>
        </UI.Container>
    );
};

export const CoownerAssemblyItem = Sentry.withProfiler(observer(CoownerAssemblyItemBase));
